import { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { StartButtonState } from './usePatrolCard';

const MINUTE_MS = 60000; // milliseconds in a minute

/**
 * Check if a value is a valid Date object
 */
function isValidDate(date: unknown): date is Date {
	return date instanceof Date && !isNaN(date.getTime());
}

export const useStartButtonState = (
	patrol: ScheduleAssignment | null | undefined,
	isAdmin: boolean,
): StartButtonState => {
	const [startButton, setStartButton] = useState<StartButtonState>({
		disabled: true,
		reason: '',
	});

	// Safely extract the time, handling null/undefined patrol
	const patrolStartTime = patrol?.timeAtWhichGuardCanBeginPatrol;

	useEffect(() => {
		// Admin can always start
		if (isAdmin) {
			setStartButton({
				disabled: false,
				reason: '',
			});
			return;
		}

		// No patrol or invalid date - disable with generic message
		if (!patrolStartTime || !isValidDate(patrolStartTime)) {
			setStartButton({
				disabled: true,
				reason: 'Patrol start time unavailable',
			});
			return;
		}

		const updateButtonState = () => {
			try {
				const currentTime = new Date();
				const minutesUntilPatrolCanStart = Math.floor(
					(patrolStartTime.getTime() - currentTime.getTime()) / MINUTE_MS,
				);

				if (minutesUntilPatrolCanStart <= 0) {
					setStartButton({
						disabled: false,
						reason: '',
					});
				} else {
					const minutesStr =
						minutesUntilPatrolCanStart === 1 ? 'minute' : 'minutes';
					setStartButton({
						disabled: true,
						reason: `Patrol can be started at ${format(
							patrolStartTime,
							'HH:mm',
						)}. (In ${minutesUntilPatrolCanStart} ${minutesStr})`,
					});
				}
			} catch (error) {
				console.error('Error calculating patrol start time:', error);
				setStartButton({
					disabled: true,
					reason: 'Error calculating start time',
				});
			}
		};

		updateButtonState(); // Initial check
		const intervalId = setInterval(updateButtonState, MINUTE_MS); // Continuous check

		return () => clearInterval(intervalId); // Cleanup on component unmount or patrol change
	}, [patrolStartTime, isAdmin]);

	return startButton;
};
